import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { ChevronLeft, FileText, Loader2 } from "lucide-react";
import { LEGAL_PAGES } from "../lib/legal";
import { useT } from "../lib/i18n";
import { api } from "../lib/shop";
import { COMPANY } from "../lib/company";
import SEO from "../components/SEO";

export default function Legal() {
  const location = useLocation();
  const { t, lang } = useT();
  const slug = location.pathname.replace(/^\/+/, "").replace(/\/+$/, "");
  const roPage = LEGAL_PAGES[slug];

  const [page, setPage] = useState(roPage);
  const [loadingTr, setLoadingTr] = useState(false);

  useEffect(() => {
    if (!roPage) return;
    if (lang === "ro") { setPage(roPage); return; }
    setLoadingTr(true);
    // Ask backend to translate-or-fetch
    api.post(`/legal/${slug}/ensure?lang=${lang}`, {
      slug,
      title: roPage.title,
      eyebrow: roPage.eyebrow,
      description: roPage.description,
      sections: roPage.sections,
    }).then((r) => {
      setPage(r.data);
    }).catch(() => {
      // fall back to RO source
      setPage(roPage);
    }).finally(() => setLoadingTr(false));
  }, [slug, lang, roPage]);

  if (!roPage) return <Navigate to="/" replace />;
  if (!page) return null;

  const dateLocale = lang === "en" ? "en-GB" : lang === "it" ? "it-IT" : "ro-RO";

  return (
    <div data-testid={`legal-page-${slug}`}>
      <SEO title={page.title} description={page.description} path={`/${slug}`} />

      <section className="border-b border-gray-200 bg-silver-shimmer">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <Link to="/" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-gray-500 hover:text-gray-900 mb-6">
            <ChevronLeft size={14} /> {t("cat.crumb.home")}
          </Link>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gray-500 flex items-center gap-2">
            <FileText size={14} strokeWidth={1.5} /> {page.eyebrow}
          </p>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter mt-3 leading-[0.95]">{page.title}</h1>
          <p className="mt-5 text-gray-600 max-w-2xl leading-relaxed">{page.description}</p>
          <p className="mt-3 text-xs uppercase tracking-[0.2em] text-gray-400">{t("legal.lastupdate")}: {new Date().toLocaleDateString(dateLocale, { day: "numeric", month: "long", year: "numeric" })}</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-20">
        {loadingTr && lang !== "ro" && (
          <div className="mb-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gray-500">
            <Loader2 size={14} className="animate-spin" /> {t("legal.translating")}
          </div>
        )}
        <article className="space-y-12">
          {(page.sections || []).map((s, i) => (
            <div key={i} className="anim-fade-up" style={{ animationDelay: `${i * 50}ms` }}>
              <h2 className="font-display text-2xl font-black tracking-tight text-gray-900">{s.h}</h2>
              <div className="mt-4 text-gray-700 leading-relaxed whitespace-pre-line text-[15px]">
                {s.p}
              </div>
            </div>
          ))}
        </article>

        <div className="mt-16 pt-10 border-t border-gray-200 text-sm text-gray-500">
          <p>
            {t("legal.contactnote.a")} <a href={`mailto:${COMPANY.contact.email}`} className="text-gray-900 font-semibold underline-offset-2 hover:underline">{COMPANY.contact.email}</a> {t("legal.contactnote.or")} <a href={`https://wa.me/${COMPANY.contact.whatsapp}`} target="_blank" rel="noopener noreferrer" className="text-gray-900 font-semibold underline-offset-2 hover:underline">{COMPANY.contact.phone}</a>.
          </p>
        </div>
      </section>
    </div>
  );
}
